import { Component, Inject } from "@angular/core";
import { FormGroup, ValidationErrors } from "@angular/forms";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { throttleTime, tap } from "rxjs/operators";

/**
 * Componente responsável por gerenciar a visibilidade e
 * validação de senhas em um formulário de dados do usuário.
 */
@Component({
    selector: "app-user-data",
    templateUrl: "./password-management-component.html",
    styleUrls: ["./password-management-component.scss"]
})
export class UserPasswordManagementComponent {
    userDataForm: FormGroup;
    currentPasswordVisible: boolean = false;
    passwordVisible: boolean = false;
    confirmPasswordVisible: boolean = false;
    EMPTY = "";

    private formFieldsErrorsMap = {};

    constructor(@Inject("formControl") public formControl: FormGroup,
                @Inject("isLdapMode") public isLdapMode: boolean,
                private readonly translationHelper: TranslationHelperService,
                @Inject(ANGULARJS_TRANSLATE) private translate: any) {
        this.userDataForm = formControl;
        this.userDataForm.valueChanges
            .pipe(throttleTime(200),
                tap(() => {
                    this.formStatusManager();
                })
            ).subscribe((x) => {});

        this.formFieldsErrorsMap = {
            password: {
                newPasswordControlRequired: this.translate.instant("manage.user.notification.preferences.requiredField"),
                newPasswordEqualsCurrent: this.translate.instant("manage.user.account.form.newPasswordEqualsCurrent")
            },
            confirmPassword: {
                confirmPasswordControlRequired: this.translate.instant("manage.user.notification.preferences.requiredField"),
                mustMatch: this.translate.instant("manage.user.account.form.confirmPasswordMustMatch")
            },
            currentPassword: {
                currentPasswordEmpty: this.translate.instant("manage.user.notification.preferences.requiredField")
            }
        }
    }

    /**
     * Essa verificação foi necessária, pois a validação do formulário de senha possui um comportamento
     * diferente.
     * Caso nenhum campo seja preenchido, nenhuma validação será obrigatória.
     * Caso a senha atual seja preenchida, os demais campos serão obrigatórios.
     * Caso a nova senha seja diferente da confirmação, uma mensagem será exibida.
     * Caso todos os campos forem preenchidos e limpos em seguida, o formulário será resetado.
     */
    private formStatusManager() {
        const currentPassword = this.userDataForm.get("currentPassword").value;
        const newPassword = this.userDataForm.get("password").value;
        const confirmPassword = this.userDataForm.get("confirmPassword").value;

        const isEmpty = (value: any) => !value?.trim();

        if (this.userDataForm.get("currentPassword").invalid) {
            this.userDataForm.get("currentPassword").markAsDirty();
        }

        if (this.userDataForm.get("password").invalid) {
            this.userDataForm.get("password").markAsDirty();
        }

        if (this.userDataForm.get("confirmPassword").invalid) {
            this.userDataForm.get("confirmPassword").markAsDirty();
        }

        if (isEmpty(currentPassword) && isEmpty(newPassword) && isEmpty(confirmPassword)) {
            this.userDataForm.reset();
        }
    }

    toggleVisibility(field: string): void {
        const property = `${field}Visible`;
        this[property] = !this[property];
    }

    getPasswordFieldErrors(field: string): string {
        const controlErrors: ValidationErrors = this.userDataForm.get(field).errors;
        let error = this.EMPTY;

        if (controlErrors != null) {
           error = this.getErrorByField(controlErrors);
        } else if (this.userDataForm?.errors) {
           error = this.getErrorByFormAndField(field);
        }

        return error;
    }

    isDependableFieldsFilled(fieldsName: string[]) {
        return fieldsName.every((fieldName) => !!this.userDataForm.get(fieldName).value);
    }

    isCurrentPasswordInvalid() {
        const currentPasswordControl = this.userDataForm.get("currentPassword");
        const userDataFormErrors = this.userDataForm.errors;

        return (currentPasswordControl.invalid && currentPasswordControl.value) ||
            userDataFormErrors?.currentPasswordEmpty;
    }

    isPasswordInvalid(): boolean {
        const passwordControl = this.userDataForm.get("password");
        const userDataFormErrors = this.userDataForm.errors;

        return (passwordControl.invalid && passwordControl.value) ||
            userDataFormErrors?.newPasswordControlRequired || userDataFormErrors?.newPasswordEqualsCurrent;
    }

    isConfirmPasswordInvalid(): boolean {
        const confirmPasswordControl = this.userDataForm.get("confirmPassword");
        const userDataFormErrors = this.userDataForm.errors;

        return (confirmPasswordControl.invalid && confirmPasswordControl.value) ||
               userDataFormErrors?.mustMatch || userDataFormErrors?.confirmPasswordControlRequired;
    }

    private getErrorByField(controlErrors: ValidationErrors): string {
        let error = this.EMPTY;
        Object.keys(controlErrors).forEach((keyError) => {
            switch (keyError) {
                case "minlength":
                    error = this.translationHelper.translateWithReplacement(
                        "manage.user.notification.preferences.passwordInvalidMinLength",
                        controlErrors[keyError].requiredLength
                    );
                    break;

                case "maxlength":
                    error = this.translationHelper.translateWithReplacement(
                        "manage.user.notification.preferences.passwordInvalidMaxLength",
                        controlErrors[keyError].requiredLength
                    );
                    break;

                case "required":
                default:
                    error = this.translate.instant("manage.user.notification.preferences.requiredField");
            }
        });

        return error;
    }

    private getErrorByFormAndField(field: string): string {
        let error = this.EMPTY;

        Object.keys(this.userDataForm?.errors).forEach((keyError) => {
            if (this.formFieldsErrorsMap[field][keyError]) {
                error = this.formFieldsErrorsMap[field][keyError]
            }
        });

        return error;
    }

}
